import { useState, useCallback } from 'react';
import { sectorService } from '../services';

/**
 * Custom hook para manejar todas las funcionalidades relacionadas con líneas base
 */
export const useBaselines = () => {
  const [allBaselinePoints, setAllBaselinePoints] = useState([]);
  const [allBaselines, setAllBaselines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processedActivities, setProcessedActivities] = useState([]);

  /**
   * Carga los puntos de actividad por sector
   */
  const loadActivityPoints = useCallback(async (sectorId) => {
    try {
      setLoading(true);
      const response = await sectorService.showActivityPoints(sectorId);
      setAllBaselinePoints(response.baselines || []);
      return response;
    } catch (error) {
      setAllBaselinePoints([]);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Carga las versiones de actividad por sector
   */
  const loadActivityVersions = useCallback(async (sectorId) => {
    try {
      setLoading(true);
      const response = await sectorService.showActivityVersions(sectorId);
      setAllBaselines(response.baselines || []);
      return response;
    } catch (error) {
      setAllBaselines([]);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Procesa y asigna los puntos de línea base a las actividades
   */
  const processBaselinePoints = useCallback(async (baselinePoints, sector) => {
    if (!baselinePoints || baselinePoints.length === 0) {
      setProcessedActivities(activities || []);
      return activities || [];
    }

    const activities = window.to_use_react_gantt.getTaskByTime();

    if (!activities || activities.length === 0) {
      setProcessedActivities([]);
      return [];
    }

    const processedActivities = activities.map((activity) => {
      const activityBaselinePoints = baselinePoints.filter(
        ({ activityId }) => activityId === activity.proplannerId
      );

      return {
        ...activity,
        baseline_points: activityBaselinePoints
      };
    });

    setProcessedActivities(processedActivities);

    return processedActivities;
  }, []);

  /**
   * Carga todos los datos de líneas base para un sector
   */
  const loadBaselineData = useCallback(
    async (sectorId) => {
      try {
        setLoading(true);
        const [basePoints, baseVersions] = await Promise.all([
          loadActivityPoints(sectorId),
          loadActivityVersions(sectorId)
        ]);

        return {
          baselinePoints: basePoints.baselines || [],
          baselineVersions: baseVersions.baselines || []
        };
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error loading baseline data:', error);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [loadActivityPoints, loadActivityVersions]
  );

  /**
   * Resetea todos los estados de líneas base
   */
  const resetBaselines = useCallback(() => {
    setAllBaselinePoints([]);
    setAllBaselines([]);
    setProcessedActivities([]);
  }, []);

  /**
   * Obtiene los puntos de línea base para una actividad específica
   */
  const getBaselinePointsForActivity = useCallback(
    (activityId) =>
      allBaselinePoints.filter((point) => point.activityId === activityId),
    [allBaselinePoints]
  );

  /**
   * Verifica si una actividad tiene puntos de línea base
   */
  const hasBaselinePoints = useCallback(
    (activityId) =>
      allBaselinePoints.some((point) => point.activityId === activityId),
    [allBaselinePoints]
  );

  /**
   * Procesa actividades con baseline points de forma manual
   * Útil cuando se necesita procesar actividades específicas
   */
  const processActivitiesWithBaselines = useCallback(
    async (activities, sector) => {
      return await processBaselinePoints(allBaselinePoints, activities, sector);
    },
    [allBaselinePoints, processBaselinePoints]
  );

  return {
    // Estados
    allBaselinePoints,
    allBaselines,
    loading,
    processedActivities,

    // Acciones
    loadActivityPoints,
    loadActivityVersions,
    processBaselinePoints,
    loadBaselineData,
    resetBaselines,
    processActivitiesWithBaselines,

    // Utilidades
    getBaselinePointsForActivity,
    hasBaselinePoints,

    // Setters para casos especiales
    setAllBaselinePoints,
    setAllBaselines
  };
};

export default useBaselines;
